// TopAreasPanel.js
import React, { useMemo, useState } from "react";
import { Box, Typography, Stack, ButtonBase } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

// ─── Design tokens (matches Sidebar light theme) ──────────────────────────────
const T = {
  bg: "#ffffff",
  surface: "#f8f9fc",
  surfaceHover: "#f0f2f7",
  border: "rgba(0,0,0,0.07)",
  borderStrong: "rgba(0,0,0,0.13)",
  text: "#1a1d23",
  textMuted: "#6b7280",
  textFaint: "#9ca3af",
  accent: "#2563eb",
  accentDim: "rgba(37,99,235,0.08)",
  accentGlow: "rgba(37,99,235,0.04)",
  chip: "#f3f4f6",
  chipActive: "rgba(37,99,235,0.1)",
  chipActiveBorder: "rgba(37,99,235,0.35)",
};

function toCSV(rows) {
  const header = ["rank", "id", "name", "match_score_0to1"];
  const lines = [header.join(",")];
  rows.forEach((r, i) => {
    lines.push([i + 1, r.id, `"${(r.name || "").replace(/"/g, '""')}"`, r.score].join(","));
  });
  return lines.join("\n");
}

export default function TopAreasPanel({ items = [], onSelect, initialCount = 10, loading = false, compareMode = false }) {
  const [count, setCount] = useState(initialCount);
  const filteredItems = useMemo(
    () => {
      const scored = items.filter((i) => Number.isFinite(i.score) && i.score !== 0);
      if (compareMode) {
        // Sort by absolute movement magnitude, gainers first then losers
        return [...scored].sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
      }
      return items.filter((i) => Number.isFinite(i.score) && i.score > 0);
    },
    [items, compareMode]
  );
  const visible = useMemo(() => filteredItems.slice(0, count), [filteredItems, count]);

  const handleExport = () => {
    const csv = toCSV(items);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "top_areas.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ px: 2.5, pb: 2, fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header row */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Typography sx={{
          fontSize: "10px", fontWeight: 700, color: T.textFaint,
          letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif",
        }}>
          {compareMode ? "Areas by Movement" : "Top Areas to Target"}
        </Typography>
  
      </Stack>

      {loading ? (
        <Box sx={{
          py: 2.5, px: 2, borderRadius: "10px",
          border: `1px solid ${T.border}`, bgcolor: T.surface, textAlign: "center",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 1.2,
        }}>
          <Box sx={{
            width: 18, height: 18, borderRadius: "50%",
            border: "2px solid rgba(0,0,0,0.15)",
            borderTopColor: T.accent, animation: "spin 0.9s linear infinite",
          }} />
          <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
            Updating top areas…
          </Typography>
        </Box>
      ) : filteredItems.length === 0 ? (
        <Box sx={{
          py: 2.5, px: 2, borderRadius: "10px",
          border: `1px dashed ${T.border}`, bgcolor: T.surface, textAlign: "center",
        }}>
          <PlaceOutlinedIcon sx={{ fontSize: 20, color: T.textFaint, mb: 0.5 }} />
          <Typography sx={{ fontSize: "12px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
            Select two answers to see top areas.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{
            borderRadius: "10px", border: `1px solid ${T.border}`,
            overflow: "auto", bgcolor: T.bg, maxHeight: 220,
          }}>
            {visible.map((area, i) => (
              <ButtonBase
                key={area.id}
                onClick={() => onSelect?.(area)}
                sx={{
                  width: "100%", display: "flex", alignItems: "center",
                  justifyContent: "space-between", px: 1.75, py: 1,
                  borderBottom: i < visible.length - 1 ? `1px solid ${T.border}` : "none",
                  transition: "background 0.12s",
                  "&:hover": { bgcolor: T.accentGlow },
                  "&:hover .area-rank": { color: T.accent },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    className="area-rank"
                    sx={{
                      fontSize: "10px", fontWeight: 700, color: T.textFaint,
                      fontFamily: "'DM Sans', sans-serif", minWidth: 18, transition: "color 0.12s",
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Typography sx={{
                    fontSize: "13px", fontWeight: 500, color: T.text,
                    fontFamily: "'DM Sans', sans-serif",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {area.name}
                  </Typography>
                </Stack>
                {area.score != null && (
                  compareMode ? (
                    <Typography sx={{
                      fontSize: "11px", fontWeight: 700, ml: 1, flexShrink: 0,
                      fontFamily: "'DM Sans', sans-serif",
                      color: area.score > 0 ? "#15803d" : area.score < 0 ? "#b91c1c" : T.textFaint,
                    }}>
                      {area.score > 0 ? "↑" : area.score < 0 ? "↓" : "→"}{" "}
                      {area.score > 0 ? "+" : ""}{(area.score * 100).toFixed(1)}pp
                    </Typography>
                  ) : (
                    <Typography sx={{
                      fontSize: "11px", fontWeight: 700, color: T.accent,
                      fontFamily: "'DM Sans', sans-serif", ml: 1, flexShrink: 0,
                    }}>
                      {typeof area.score === "number" ? `${(area.score * 100).toFixed(1)}%` : area.score}
                    </Typography>
                  )
                )}
              </ButtonBase>
            ))}
          </Box>

        </>
      )}
    </Box>
  );
}
